import { Tooltip, Typography } from "@mui/material";
import MoedaText from "../ui/inputs/moedaText";
import { map, sumBy } from "lodash";
import Transacao from "../../../models/transacao";
import Ticker from "../ui/ticker";
import {
  ClasseAtivo,
  Mercado,
  TipoOperacao,
  Trade,
} from "../../../models/enum";
import { ClasseChip } from "../ui/classeChip";
import TabelaBase from "./tabelaBase";
import InteiroText from "../ui/inputs/inteiroText";
import ChipOperacao from "../ui/chipOperacao";

type Props = {
  transacoes: Transacao[] | undefined;
  maxWidth?: number;
  extendida?: boolean;
};

export default function TabelaExtrato({
  transacoes,
  maxWidth,
  extendida = false,
}: Props) {
  const header: {
    titulo: string;
    key: string;
    ordem: number;
    align: "left" | "center" | "right";
  }[] = extendida
    ? [
        { titulo: "Dia", key: "dia", ordem: 1, align: "center" },
        { titulo: "Ativo", key: "ativo", ordem: 2, align: "center" },
        { titulo: "Classe", key: "classe", ordem: 3, align: "center" },
        { titulo: "Class.", key: "classificacao", ordem: 4, align: "center" },
        { titulo: "Merc.", key: "mercado", ordem: 5, align: "center" },
        { titulo: "Preço Médio", key: "precoMedio", ordem: 6, align: "right" },
        { titulo: "Operação", key: "tipoOperacao", ordem: 7, align: "center" },
        { titulo: "Qtd.", key: "quantidade", ordem: 8, align: "right" },
        {
          titulo: "Preço Unitário",
          key: "precoUnitario",
          ordem: 9,
          align: "right",
        },
        { titulo: "Resultado", key: "resultado", ordem: 10, align: "right" },
      ]
    : [
        { titulo: "Dia", key: "dia", ordem: 1, align: "center" },
        { titulo: "Ativo", key: "ativo", ordem: 2, align: "center" },
        { titulo: "Classe", key: "classe", ordem: 3, align: "center" },
        { titulo: "Class.", key: "classificacao", ordem: 4, align: "center" },
        { titulo: "Merc.", key: "mercado", ordem: 5, align: "center" },
        { titulo: "Resultado", key: "resultado", ordem: 6, align: "right" },
      ];
  const rows = map(transacoes, (transacao) => ({
    dia: celulaDia(transacao),
    ativo: celulaAtivo(transacao),
    classe: celulaClasse(transacao),
    classificacao: celulaClassificacao(transacao),
    mercado: celulaMercado(transacao),
    precoMedio: celulaPrecoMedio(transacao),
    tipoOperacao: celulaTipoOperacao(transacao),
    quantidade: celulaQuantidade(transacao),
    precoUnitario: celulaPrecoUnitario(transacao),
    resultado: celulaResultado(transacao),
  }));

  const resultadoTotal = sumBy(transacoes, "resultado");
  const footer = {
    resultado: (
      <Typography
        variant="dataMono"
        className={resultadoTotal >= 0 ? "success" : "error"}
      >
        <MoedaText value={resultadoTotal} displayType={"text"}></MoedaText>
      </Typography>
    ),
  };

  return (
    <TabelaBase
      header={header}
      rows={rows}
      footer={footer}
      maxWidth={maxWidth}
    />
  );

  function mostrarCalculo(transacao: Transacao): JSX.Element {
    switch (transacao.mercado) {
      case Mercado.A_VISTA:
      case Mercado.OPCOES:
        if (transacao.daytradeSwingtrade === Trade.Swingtrade) {
          const precoUnitario = transacao.operacao!.precoUnitario;
          const precoMedio = transacao.posicao!.precoMedio;
          const quantidade = transacao.operacao!.quantidade;
          const taxas = transacao.operacao!.taxas;

          return transacao.operacao!.tipoOperacao === TipoOperacao.VENDA ? (
            <>
              (
              {
                <MoedaText
                  value={precoUnitario}
                  displayType={"text"}
                ></MoedaText>
              }{" "}
              -{" "}
              {<MoedaText value={precoMedio} displayType={"text"}></MoedaText>})
              x {quantidade} -{" "}
              {<MoedaText value={taxas} displayType={"text"}></MoedaText>}
            </>
          ) : (
            <>
              ({<MoedaText value={precoMedio} displayType={"text"}></MoedaText>}{" "}
              -{" "}
              {
                <MoedaText
                  value={precoUnitario}
                  displayType={"text"}
                ></MoedaText>
              }
              ) x {quantidade} -{" "}
              {<MoedaText value={taxas} displayType={"text"}></MoedaText>}
            </>
          );
        } else {
          return <></>;
        }
      default:
        return <></>;
    }
  }

  function celulaResultado(transacao: Transacao): JSX.Element {
    return (
      <Tooltip
        title={
          transacao.operacao && transacao.posicao
            ? mostrarCalculo(transacao)
            : ""
        }
      >
        <Typography
          variant="dataMono"
          className={transacao.resultado > 0 ? "success" : "error"}
        >
          <MoedaText
            value={transacao.resultado}
            displayType={"text"}
          ></MoedaText>
        </Typography>
      </Tooltip>
    );
  }

  function celulaPrecoUnitario(transacao: Transacao): JSX.Element {
    return (
      <Typography variant="dataMono">
        <MoedaText
          value={transacao.operacao?.precoUnitario}
          displayType={"text"}
        ></MoedaText>
      </Typography>
    );
  }

  function celulaQuantidade(transacao: Transacao): JSX.Element {
    return (
      <Typography variant="dataMono">
        <InteiroText
          value={transacao.operacao?.quantidade}
          displayType={"text"}
        ></InteiroText>
      </Typography>
    );
  }

  function celulaTipoOperacao(transacao: Transacao): JSX.Element {
    if (!!transacao.operacao)
      return <ChipOperacao tipoOperacao={transacao.operacao?.tipoOperacao} />;
    else return <></>;
  }

  function celulaPrecoMedio(transacao: Transacao): JSX.Element {
    return (
      <Typography variant="dataMono">
        <MoedaText
          value={transacao.posicao?.precoMedio}
          displayType={"text"}
        ></MoedaText>
      </Typography>
    );
  }

  function celulaMercado(transacao: Transacao): JSX.Element {
    return <Typography>{transacao.mercado}</Typography>;
  }

  function celulaClassificacao(transacao: Transacao): JSX.Element {
    return (
      <Typography>
        {transacao.classeAtivo === ClasseAtivo.FII ||
        transacao.classeAtivo === ClasseAtivo.FIAGRO
          ? "FII/Fiagro"
          : transacao.daytradeSwingtrade === Trade.Daytrade
          ? "Daytrade"
          : "Comuns"}
      </Typography>
    );
  }

  function celulaClasse(transacao: Transacao): JSX.Element {
    return <ClasseChip classeAtivo={transacao.classeAtivo} />;
  }

  function celulaAtivo(transacao: Transacao): JSX.Element {
    return <Ticker codigoAtivo={transacao.codigoAtivo} />;
  }

  function celulaDia(transacao: Transacao): JSX.Element {
    return (
      <Typography variant="dataMono">
        {transacao.dataTransacao.format("DD/MM")}
      </Typography>
    );
  }
}
